import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  MapPin,
  Clock,
  Users,
  FileHeart,
  ChevronRight,
  X,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { cn, formatTimestamp, getSeverityColor, getSeverityBg, getStatusColor, getCategoryIcon, API_BASE_URL, SOCKET_URL } from '../lib/utils';
import { io } from 'socket.io-client';
import { useAppSelector } from '../store';
import { useActiveEvent } from '../contexts/EventContext';
import type { Incident, IncidentSeverity, IncidentCategory } from '../types';
import MapLocationPicker from '../components/map/MapLocationPicker';
import CommandActionBar from '../components/command/CommandActionBar';
import { looksLikeCommandAction } from '../lib/commandActions';

const severityOptions: IncidentSeverity[] = ['critical', 'high', 'medium', 'low', 'info'];
const categoryOptions: IncidentCategory[] = ['fire', 'medical', 'traffic', 'criminal', 'hazmat', 'natural_disaster', 'infrastructure', 'public_order', 'other'];

export default function IncidentsPage() {
  const navigate = useNavigate();
  const { token } = useAppSelector((state) => state.auth);
  const { activeEventId } = useActiveEvent();
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  // Default: hide closed incidents (status 'active' = open + supervision)
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [showNewIncident, setShowNewIncident] = useState(false);
  const [creating, setCreating] = useState(false);

  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newCategory, setNewCategory] = useState<IncidentCategory>('other');
  const [newSeverity, setNewSeverity] = useState<IncidentSeverity>('medium');
  const [newAddress, setNewAddress] = useState('');
  const [newLatitude, setNewLatitude] = useState(0);
  const [newLongitude, setNewLongitude] = useState(0);
  const [showMapPicker, setShowMapPicker] = useState(false);

  const fetchIncidents = useCallback(async () => {
    try {
      setLoading(true);
      const url = activeEventId 
        ? `${API_BASE_URL}/incidents?eventId=${activeEventId}`
        : `${API_BASE_URL}/incidents`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setIncidents(data.incidents);
      }
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  }, [token, activeEventId]);

  useEffect(() => {
    fetchIncidents();
    // Poll every 30 seconds as a fallback
    const interval = setInterval(fetchIncidents, 30000);
    return () => clearInterval(interval);
  }, [fetchIncidents]);

  // Real-time socket updates: patch local list immediately on incident:updated
  useEffect(() => {
    const token = localStorage.getItem('c2_token');
    const socket = io(SOCKET_URL, { transports: ['websocket'], auth: { token } });
    socket.on('incident:updated', (updated: Incident) => {
      setIncidents((prev) =>
        prev.map((inc) => (inc.id === updated.id ? updated : inc))
      );
    });
    socket.on('incident:created', (created: Incident) => {
      setIncidents((prev) =>
        prev.find((i) => i.id === created.id) ? prev : [created, ...prev]
      );
    });
    return () => { socket.disconnect(); };
  }, []);

  const filtered = incidents.filter((inc) => {
    const textQuery = looksLikeCommandAction(search) ? '' : search;
    const searchActive = textQuery.trim().length > 0;
    if (searchActive) {
      // Search overrides status filter so closed incidents can still be found
      const q = textQuery.toLowerCase();
      const matches = inc.title.toLowerCase().includes(q)
        || inc.id.toLowerCase().includes(q)
        || (inc.description || '').toLowerCase().includes(q)
        || (inc.location?.address || '').toLowerCase().includes(q);
      if (!matches) return false;
    } else {
      // Apply status filter when not searching
      if (statusFilter === 'active' && inc.status === 'closed') return false;
      if (statusFilter === 'supervision' && inc.status !== 'supervision') return false;
      if (statusFilter === 'closed' && inc.status !== 'closed') return false;
      // 'all' shows everything, no filter needed
    }
    if (severityFilter !== 'all' && inc.severity !== severityFilter) return false;
    return true;
  });

  const handleCreateIncident = async () => {
    setCreating(true);
    try {
      const res = await fetch(`${API_BASE_URL}/incidents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          eventId: activeEventId || '',
          title: newTitle,
          description: newDescription,
          category: newCategory,
          severity: newSeverity,
          location: { latitude: newLatitude, longitude: newLongitude, address: newAddress },
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setIncidents((prev) => [data.incident, ...prev]);
        setShowNewIncident(false);
        setNewTitle('');
        setNewDescription('');
        setNewAddress('');
        setNewLatitude(0);
        setNewLongitude(0);
        setNewCategory('other');
        setNewSeverity('medium');
      }
    } catch { /* ignore */ } finally {
      setCreating(false);
    }
  };

  return (
    <div className="h-full flex flex-col gap-3">
      {/* Header - fixed */}
      <div className="flex items-center justify-between flex-shrink-0">
        <div>
          <h1 className="text-lg font-bold text-foreground">Incidents</h1>
          <p className="text-xs text-muted-foreground">{filtered.length} active</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchIncidents} className="p-2 rounded-lg hover:bg-accent transition-colors" title="Refresh">
            <RefreshCw className={cn('w-4 h-4 text-muted-foreground', loading && 'animate-spin')} />
          </button>
          <button
            onClick={() => setShowNewIncident(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            New
          </button>
        </div>
      </div>

      {/* Filters - fixed */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <CommandActionBar
          className="flex-1"
          incidents={incidents}
          searchQuery={search}
          onSearchChange={setSearch}
          onOpenIncident={(id) => navigate(`/workspace/incidents/${id}`)}
          inputClassName="py-1.5 rounded-lg"
        />
        <select
          value={severityFilter}
          onChange={(e) => setSeverityFilter(e.target.value)}
          className="px-2 py-1.5 rounded-lg bg-card border border-border text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          <option value="all">Severity</option>
          {severityOptions.map((s) => (
            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-2 py-1.5 rounded-lg bg-card border border-border text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
          title="Status filter (search ignores this)"
        >
          <option value="active">Active</option>
          <option value="supervision">Supervision</option>
          <option value="closed">Closed</option>
          <option value="all">All Incidents</option>
        </select>
      </div>

      {/* Scrollable list */}
      <div className="flex-1 min-h-0 glass-panel overflow-y-auto divide-y divide-border">
        {loading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground text-sm">No incidents match.</div>
        ) : (
          filtered.map((incident) => (
            <button
              key={incident.id}
              onClick={() => navigate(`/workspace/incidents/${incident.id}`)}
              className="w-full px-3 py-2.5 hover:bg-accent/50 transition-colors text-left"
            >
              <div className="flex items-center gap-2.5">
                <span className="text-base">{getCategoryIcon(incident.category)}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="text-[10px] font-mono text-muted-foreground">{incident.id}</span>
                    <span className={cn('text-[10px] px-1.5 py-0.5 rounded-full border font-medium', getSeverityBg(incident.severity), getSeverityColor(incident.severity))}>
                      {incident.severity.toUpperCase()}
                    </span>
                    <span className={cn('text-[10px] font-medium', getStatusColor(incident.status))}>
                      {incident.status.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                  <p className="text-xs font-medium text-foreground truncate">{incident.title}</p>
                  <div className="flex items-center gap-3 mt-0.5 text-[10px] text-muted-foreground">
                    <span className="flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{incident.location.address}</span>
                    <span className="flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" />{formatTimestamp(incident.reportedAt)}</span>
                    <span className="flex items-center gap-0.5"><Users className="w-2.5 h-2.5" />{incident.assignedUnits.length}</span>
                    {(incident.manualPatients?.length || 0) > 0 && (
                      <span className="flex items-center gap-0.5 text-rose-400">
                        <FileHeart className="w-2.5 h-2.5" />{incident.manualPatients!.length}
                      </span>
                    )}
                  </div>
                </div>
                <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
              </div>
            </button>
          ))
        )}
      </div>

      {/* New Incident Modal */}
      {showNewIncident && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-lg glass-panel border border-border p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-bold text-foreground">Create New Incident</h2>
              <button onClick={() => setShowNewIncident(false)} className="p-1 hover:bg-accent rounded-lg"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); handleCreateIncident(); }} className="space-y-3">
              <div>
                <label className="text-xs font-medium text-foreground block mb-1">Title</label>
                <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} className="w-full px-3 py-1.5 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50" placeholder="Incident title" required />
              </div>
              <div>
                <label className="text-xs font-medium text-foreground block mb-1">Description</label>
                <textarea value={newDescription} onChange={(e) => setNewDescription(e.target.value)} rows={2} className="w-full px-3 py-1.5 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none" placeholder="Describe the incident..." required />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-medium text-foreground block mb-1">Category</label>
                  <select value={newCategory} onChange={(e) => setNewCategory(e.target.value as IncidentCategory)} className="w-full px-3 py-1.5 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50">
                    {categoryOptions.map((c) => <option key={c} value={c}>{getCategoryIcon(c)} {c.replace('_', ' ')}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-foreground block mb-1">Severity</label>
                  <select value={newSeverity} onChange={(e) => setNewSeverity(e.target.value as IncidentSeverity)} className="w-full px-3 py-1.5 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50">
                    {severityOptions.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-foreground block mb-1">Location</label>
                <div className="flex gap-2">
                  <input value={newAddress} onChange={(e) => setNewAddress(e.target.value)} className="flex-1 px-3 py-1.5 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50" placeholder="Address or coordinates" required />
                  <button
                    type="button"
                    onClick={() => setShowMapPicker(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-primary/30 bg-primary/5 text-primary text-xs font-semibold hover:bg-primary/10 transition-colors flex-shrink-0"
                    title="Select location on map"
                  >
                    <MapPin className="w-3.5 h-3.5" />
                    Map
                  </button>
                </div>
                {newLatitude !== 0 && newLongitude !== 0 && (
                  <p className="text-[10px] text-muted-foreground mt-1 font-mono">
                    Coords: {newLatitude.toFixed(6)}, {newLongitude.toFixed(6)}
                  </p>
                )}
              </div>
              <div className="flex gap-2 pt-1">
                <button type="button" onClick={() => setShowNewIncident(false)} className="flex-1 py-1.5 rounded-lg border border-border text-sm font-medium text-muted-foreground hover:bg-accent transition-colors">Cancel</button>
                <button type="submit" disabled={creating} className="flex-1 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5">
                  {creating && <Loader2 className="w-3 h-3 animate-spin" />}
                  {creating ? 'Creating...' : 'Create Incident'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showMapPicker && (
        <MapLocationPicker
          open={showMapPicker}
          onClose={() => setShowMapPicker(false)}
          onSelect={(lat, lng, addr) => {
            setNewLatitude(lat);
            setNewLongitude(lng);
            setNewAddress(addr);
          }}
        />
      )}
    </div>
  );
}
